import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { StaffService } from 'src/app/core/api/staff.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { LoadingSpinnerService } from 'src/app/shared/loading-spinner/loading-spinner.service';
import {
  CLINIC_CHANGE_FLASH_MESSAGE_STORAGE_KEY,
  ClinicChangeFlashMessage
} from './clinic-change-flash-message';

@Component({
  selector: 'csi-change-clinic-modal',
  templateUrl: './change-clinic-modal.component.html',
  styleUrls: ['./change-clinic-modal.component.css']
})
export class ChangeClinicModalComponent implements OnInit {
  public selectedClinicId = new FormControl();
  public sortedClinics: Array<{ id: string; name: string }> = [];
  touched = false;

  constructor(
    public staffService: StaffService,
    public confirmModalService: ConfirmModalService,
    private loadingSpinnerService: LoadingSpinnerService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.sortedClinics = this.buildSortedClinics();

    this.selectedClinicId.valueChanges.subscribe(() => {
      this.touched = true;
    });
  }

  private buildSortedClinics(): Array<{ id: string; name: string }> {
    const ownedClinics = JSON.parse(this.staffService.staff.ownedClinics || '{}');

    return Object.keys(ownedClinics)
      .map(id => ({ id, name: ownedClinics[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  static open(dialog: MatDialog): MatDialogRef<ChangeClinicModalComponent> {
    return dialog.open(ChangeClinicModalComponent, {
      minWidth: '19.1rem'
    });
  }

  changeClinic() {
    const ownedClinics = JSON.parse(this.staffService.staff.ownedClinics || '{}');
    const clinicName = ownedClinics[this.selectedClinicId.value];

    const title = clinicName ? 'Change Clinic to {{clinicName}}' : 'Create New Clinic';
    const message = clinicName ? 'changeClinicConfirmMessage' : 'createClinicConfirmMessage';
    const translationParams = clinicName ? { titleParams: { clinicName }, bodyParams: { clinicName } } : undefined;

    const confirmText = clinicName
      ? 'Change Clinic'
      : 'Create New Clinic';

    const cancelText = 'Cancel';

    this.confirmModalService
      .show(title, message, confirmText, cancelText, undefined, undefined, undefined, translationParams)
      .subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.loadingSpinnerService.show();

      if (this.staffService.staff.clinic) {
        ownedClinics[this.staffService.staff.clinic.id] = this.staffService.staff.clinic.name;
      }

      const flashMessage: ClinicChangeFlashMessage = {
        message: clinicName
          ? this.translocoService.translate('Clinic changed to {{clinicName}}', { clinicName })
          : this.translocoService.translate('New clinic created')
      };

      this.staffService
        .updateDoctor({
          id: this.staffService.staff.id,
          ownedClinics: JSON.stringify(ownedClinics),
          doctorClinicId: this.selectedClinicId.value || null
        })
        .subscribe(
          () => {
            // Store message before full-page redirect so it can be shown on /home
            // after clinic-scoped state reinitializes. Redirect immediately so the
            // user cannot keep editing entities that belong to the previous clinic.
            // No spinner.hide() on success: the navigation unloads the page (same as
            // the previous reload() behavior).
            sessionStorage.setItem(
              CLINIC_CHANGE_FLASH_MESSAGE_STORAGE_KEY,
              JSON.stringify(flashMessage)
            );
            window.location.href = '/home';
          },
          () => this.loadingSpinnerService.hide()
        );
    });
  }
}
